import { APIService, APIError } from '../../../lib/api/APIService';

// Case Study API class extending the base APIService
class CaseStudyAPI extends APIService {
  /**
   * Graph LLM calls: no auto-retry storm (isRetry: true).
   * Client wait must be >= DO worker window so we don't AbortError before the server replies.
   */
  _graphPostOpts(timeoutMs = 90000) {
    return { timeout: timeoutMs, isRetry: true };
  }

  async getCaseStudies(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    const endpoint = queryString ? `/api/case-study/?${queryString}` : '/api/case-study/';
    return this.get(endpoint);
  }

  async getCaseStudyById(id) {
    return this.get(`/api/case-study/${id}`);
  }

  // New method to fetch figures/exhibits for a specific case study
  async getCaseStudyFigures(caseStudyId) {
    try {
      return this.get(`/api/case-study/${caseStudyId}/figures`);
    } catch (error) {
      // If endpoint doesn't exist yet, return empty array
      if (error.status === 404) {
        console.warn('Figures endpoint not implemented yet');
        return [];
      }
      throw error;
    }
  }

  async getCaseStudyHealth() {
    return this.get('/api/case-study/health');
  }

  async getCaseQuizCounts(caseStudyId) {
    return this.get(`/api/case-study/${caseStudyId}/quiz-counts`);
  }

  async getCaseQuizzes(caseStudyId) {
    return this.get(`/api/case-study/${caseStudyId}/quizzes`);
  }

  async startCaseQuizAttempt(caseStudyId, payload = {}) {
    return this.post(`/api/case-study/${caseStudyId}/quiz-attempts`, payload);
  }

  async submitCaseQuizAnswer(attemptId, payload) {
    return this.post(`/api/case-study/quiz-attempts/${attemptId}/answers`, payload);
  }

  async completeCaseQuizAttempt(attemptId) {
    return this.post(`/api/case-study/quiz-attempts/${attemptId}/complete`, {});
  }

  async getCaseTutorialCounts(caseStudyId) {
    return this.get(`/api/case-study/${caseStudyId}/tutorial-counts`);
  }

  async getCaseTutorials(caseStudyId) {
    return this.get(`/api/case-study/${caseStudyId}/tutorials`);
  }

  async startCaseTutorialAttempt(caseStudyId) {
    return this.post(`/api/case-study/${caseStudyId}/tutorial-attempts`, {});
  }

  async submitCaseTutorialResponse(attemptId, payload) {
    return this.post(`/api/case-study/tutorial-attempts/${attemptId}/responses`, payload, {
      timeout: 30000,
      isRetry: true,
    });
  }

  async extractCaseTutorialResponse(attemptId, responseId) {
    return this.post(`/api/case-study/tutorial-attempts/${attemptId}/extract`, {
      response_id: responseId,
    }, {
      timeout: 90000,
      isRetry: true,
    });
  }

  async completeCaseTutorialAttempt(attemptId) {
    return this.post(`/api/case-study/tutorial-attempts/${attemptId}/complete`, {}, {
      timeout: 90000,
      isRetry: true,
    });
  }

  async getCaseDiscussion(caseStudyId) {
    return this.get(`/api/case-study/${caseStudyId}/discussion`);
  }

  async startCaseDiscussionAttempt(caseStudyId) {
    return this.post(`/api/case-study/${caseStudyId}/discussion-attempts`, {});
  }

  async submitCaseDiscussionUtterance(attemptId, payload) {
    return this.post(`/api/case-study/discussion-attempts/${attemptId}/utterances`, payload, {
      timeout: 90000,
      isRetry: true,
    });
  }

  async completeCaseDiscussionAttempt(attemptId) {
    return this.post(`/api/case-study/discussion-attempts/${attemptId}/complete`, {}, {
      timeout: 60000,
      isRetry: true,
    });
  }

  async getCaseDiscussionAttempt(attemptId) {
    return this.get(`/api/case-study/discussion-attempts/${attemptId}`);
  }

  async renderDiscussionLine(attemptId, payload) {
    return this.post(`/api/case-study/discussion-attempts/${attemptId}/render-line`, payload, {
      timeout: 20000,
      isRetry: true,
    });
  }

  async extractDiscussionUtterance(attemptId, utteranceId) {
    return this.post(`/api/case-study/discussion-attempts/${attemptId}/extract`, {
      utterance_id: utteranceId,
    }, {
      timeout: 90000,
      isRetry: true,
    });
  }

  async generateDiscussionSpeech(text, voice) {
    return this.post('/api/case-study/discussion/generate-speech', { text, voice }, {
      timeout: 60000,
      isRetry: true,
    });
  }

  /** POST structured AI feedback. Legacy non-graph cases only. */
  async submitCaseFeedback(caseStudyId, payload, options = {}) {
    return this.post(
      `/api/case-study/${caseStudyId}/feedback`,
      payload,
      { timeout: 90000, isRetry: true, ...options }
    );
  }

  /** Graph-driven: phase 1 — save attempt (fast). */
  async createCaseAttempt(caseStudyId, payload) {
    return this.post(
      `/api/case-study/${caseStudyId}/attempts`,
      payload,
      this._graphPostOpts(30000)
    );
  }

  /** Graph-driven: extraction pass 1 (single LLM call). */
  async extractCaseAttemptPass1(attemptId) {
    return this.post(
      `/api/case-study/attempts/${attemptId}/extract/pass1`,
      {},
      this._graphPostOpts()
    );
  }

  /** Graph-driven: extraction pass 2 + consensus + essay graph writes (single LLM call). */
  async extractCaseAttemptPass2(attemptId) {
    return this.post(
      `/api/case-study/attempts/${attemptId}/extract/pass2`,
      {},
      this._graphPostOpts()
    );
  }

  /** @deprecated Use extractCaseAttemptPass1 + Pass2 */
  async extractCaseAttempt(attemptId) {
    return this.post(
      `/api/case-study/attempts/${attemptId}/extract`,
      {},
      this._graphPostOpts()
    );
  }

  /** Graph-driven: structured feedback (pass 3). */
  async completeCaseAttemptFeedback(attemptId) {
    return this.post(
      `/api/case-study/attempts/${attemptId}/feedback`,
      {},
      this._graphPostOpts()
    );
  }

  /** Fire-and-forget graph pipeline: pass1 then pass2 (each under worker timeout). */
  async processGraphAttempt(attemptId) {
    await this.extractCaseAttemptPass1(attemptId);
    await this.extractCaseAttemptPass2(attemptId);
  }

  async _withTimeoutRetry(label, fn, onProgress) {
    try {
      return await fn();
    } catch (err) {
      const timedOut =
        err?.name === 'AbortError' ||
        /aborted|timeout/i.test(String(err?.message || ''));
      if (!timedOut) throw err;
      if (typeof onProgress === 'function') {
        onProgress(`${label} took longer than expected — retrying…`);
      }
      await new Promise((r) => setTimeout(r, 2500));
      return fn();
    }
  }

  /**
   * Full graph-driven user feedback (split across DO ~60s per request):
   * create → pass1 → pass2 (DB graph events + isomorphism) → feedback phase.
   * onProgress(message) updates loading copy between calls.
   */
  async submitGraphCaseFeedback(caseStudyId, payload, { onProgress } = {}) {
    const token = this.getAccessToken();
    if (!token) {
      throw new APIError('Authentication failed', 401, { requiresLogin: true });
    }
    const report = (msg) => {
      if (typeof onProgress === 'function') onProgress(msg);
    };
    report('Saving your attempt...');
    const created = await this.createCaseAttempt(caseStudyId, payload);
    report('Mapping your essay onto the teaching graph… (can take up to ~1 min)');
    await this._withTimeoutRetry(
      'Graph mapping',
      () => this.extractCaseAttemptPass1(created.attempt_id),
      onProgress
    );
    report('Running isomorphism check and writing graph events…');
    const pass2 = await this._withTimeoutRetry(
      'Isomorphism check',
      () => this.extractCaseAttemptPass2(created.attempt_id),
      onProgress
    );
    report('Generating graph-driven quality feedback…');
    const feedback = await this._withTimeoutRetry(
      'Quality feedback',
      () => this.completeCaseAttemptFeedback(created.attempt_id),
      onProgress
    );
    return {
      ...feedback,
      attempt_id: created.attempt_id,
      session_id: created.session_id,
      case_study_id: caseStudyId,
      isomorphism_score: feedback.isomorphism_score ?? pass2?.isomorphism_score,
      consensus: feedback.consensus ?? pass2?.consensus,
      graph_feedback: true,
    };
  }
}

// Create and export case study API instance
export const caseStudyAPI = new CaseStudyAPI();
